// Content taxonomy for the unified Resources hub (the blog now lives here as the
// "article" category). Must stay in sync with the dynamic_pages.category CHECK
// constraint in supabase/migrations and the admin editor's category selector.
export type ResourceCategory = 'article' | 'guide' | 'state-guide' | 'tool' | 'comparison';

export interface ResourceCard {
  id: string;
  title: string;
  description: string;
  url: string;
  category: ResourceCategory;
  image?: string;
  date?: string;
  tags?: string[];
  featured?: boolean;
}

export const FEATURED_RESOURCES: ResourceCard[] = [
  {
    id: 'featured-agreement-template',
    title: 'NP Collaborative Agreement Template (Free Download)',
    description: 'Get a free 50+ page collaborative agreement template covering all 50 states. Includes sample language, filing requirements and NP-protective clauses.',
    url: '/collaborative-agreement-template',
    category: 'guide',
    image: '/og-image.png',
    tags: ['template', 'collaborative agreement', 'free download'],
    featured: true,
  },
  {
    id: 'featured-best-platforms',
    title: 'Best Collaborating Physician Platforms (2026)',
    description: 'Honest comparison of the top NP collaborating physician platforms. Transparent all-inclusive pricing starting at $424/mo.',
    url: '/resources/best-collaborating-physician-platforms',
    category: 'comparison',
    image: '/og-image.png',
    tags: ['comparison', 'platforms', 'pricing'],
    featured: true,
  },
  {
    id: 'featured-savings-calculator',
    title: 'NP Collaboration Savings Calculator',
    description: 'Calculate how much you can save on collaboration fees. Compare costs and find the best rates in your state.',
    url: '/savings-calculator',
    category: 'tool',
    image: '/og-image.png',
    tags: ['calculator', 'savings', 'cost comparison'],
    featured: true,
  },
  {
    id: 'featured-find-physician',
    title: 'Find a Collaborating Physician by State',
    description: 'Browse our network of verified collaborating physicians by state. Find the perfect match for your NP practice needs.',
    url: '/collaborating-physician',
    category: 'state-guide',
    image: '/og-image.png',
    tags: ['find physician', 'state guide', 'network'],
    featured: true,
  },
];

export const CATEGORY_LABELS: Record<ResourceCategory, string> = {
  article: 'Articles',
  guide: 'Guides',
  'state-guide': 'State Guides',
  tool: 'Tools',
  comparison: 'Comparisons',
};

// Fallback categorization from a URL path. Used only when a row has no explicit
// stored `category` (e.g. before the migration backfill runs, or for legacy rows).
// Prefer the stored dynamic_pages.category whenever it is present.
export function categorizeByUrl(urlPath: string): ResourceCategory {
  if (urlPath.startsWith('/blog')) return 'article';
  if (urlPath.startsWith('/collaborating-physician/')) return 'state-guide';
  if (urlPath.startsWith('/resources/')) return 'guide';
  return 'guide';
}

// Normalize a possibly-null stored category into a valid ResourceCategory,
// falling back to URL inference. Keeps the hub working across the migration boundary.
export function resolveCategory(
  storedCategory: string | null | undefined,
  urlPath: string,
): ResourceCategory {
  const valid: ResourceCategory[] = ['article', 'guide', 'state-guide', 'tool', 'comparison'];
  if (storedCategory && valid.includes(storedCategory as ResourceCategory)) {
    return storedCategory as ResourceCategory;
  }
  return categorizeByUrl(urlPath);
}
